import { StickyNote } from "lucide-react"
import type { ClientTheme } from "@/app/lib/themes"
import { HoverTooltip } from "@/app/components/HoverTooltip"

interface NoteBadgeProps {
  note: string
  theme: ClientTheme
  /** Width the truncated preview may occupy inside its cell. */
  maxWidth?: string
}

/**
 * Compact note indicator for dense tables: amber chip with a sticky-note icon
 * and a one-line truncated preview; the full note appears in a hover tooltip.
 * Reusable anywhere a note needs surfacing (inventory today, wells later).
 */
export function NoteBadge({ note, theme, maxWidth = "170px" }: NoteBadgeProps) {
  return (
    <HoverTooltip content={note} theme={theme}>
      <span
        tabIndex={0}
        className="inline-flex items-center gap-1.5 rounded-md px-2 py-1"
        style={{
          maxWidth,
          backgroundColor: `${theme.noteAccent}1F`,
          border: `1px solid ${theme.noteAccent}45`,
          cursor: "default",
        }}
      >
        <StickyNote size={12} style={{ color: theme.noteAccent, flexShrink: 0 }} />
        <span
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: "12px",
            color: theme.textPrimary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {note}
        </span>
      </span>
    </HoverTooltip>
  )
}

interface NoteIconProps {
  note: string
  theme: ClientTheme
}

/**
 * Bare sticky-note icon that surfaces the full note in a hover tooltip — no
 * inline preview text. Sits beside a value (e.g. the STR cell) as a compact
 * "this row has a note" marker where a full NoteBadge chip would be too wide.
 */
export function NoteIcon({ note, theme }: NoteIconProps) {
  return (
    <HoverTooltip content={note} theme={theme}>
      <span
        tabIndex={0}
        className="inline-flex items-center"
        aria-label="View note"
        style={{ cursor: "default" }}
      >
        <StickyNote size={13} style={{ color: theme.noteAccent, flexShrink: 0 }} />
      </span>
    </HoverTooltip>
  )
}
